
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Cpu, Search, RotateCcw } from 'lucide-react';
import { deviceList } from '../../services/deviceService';
import './DeviceManagementRedesign.css';

const kinds = ['플러그', '스위치', '감지기'];
const PAGE_SIZE = 10;
const PAGE_GROUP_SIZE = 5;
const PLUG_PREFIX = /^DAWONDNS-B350N_NBX-\s*/;

const getStatusText = (value) => {
  if (value === 1 || value === '1') return '정상';
  if (value === 0 || value === '0') return '비정상';
  return '확인 필요';
};

const getElapsedTime = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString.replace(' ', 'T'));
  const timestamp = date.getTime();

  if (!Number.isFinite(timestamp)) return '-';

  const diff = Date.now() - timestamp;
  if (diff < 0) return '방금 전';

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  if (hours < 24) return `${hours}시간 전`;

  return `${days}일 전`;
};

export default function DeviceManagementRedesign() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [devices, setDevices] = useState([]);
  const kindFromUrl = searchParams.get('kind');

  const kind = kinds.includes(kindFromUrl)
    ? kindFromUrl
    : '플러그';

  const savedQuery = searchParams.get('query') || '';
  const [inputQuery, setInputQuery] = useState(savedQuery);
  const query = savedQuery;

  useEffect(() => {
    setInputQuery(savedQuery);
  }, [savedQuery]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pageFromUrl = Number(searchParams.get('page'));
  const currentPage =
    Number.isInteger(pageFromUrl) && pageFromUrl > 0
      ? pageFromUrl
      : 1;

  useEffect(() => {
    let active = true;

    const loadDevices = async () => {
      try {
        setLoading(true);
        setError('');

        const token = localStorage.getItem('token');
        const response = await deviceList(token);

        if (!active) return;

        if (!response?.success || !Array.isArray(response.device)) {
          throw new Error(response?.message || '장비 목록 조회 실패');
        }

        setDevices(response.device);
      } catch (err) {
        if (!active) return;
        console.error('장비 목록 조회 실패:', err);
        setError(err.message || '장비 목록을 불러오지 못했습니다.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDevices();

    return () => {
      active = false;
    };
  }, []);

  const changePage = (nextPage) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  const changeKind = (nextKind) => {
    const nextParams = new URLSearchParams(searchParams);

    nextParams.set('kind', nextKind);
    nextParams.set('page', '1');
    nextParams.delete('query');

    setSearchParams(nextParams);
  };

  const changeQuery = (nextQuery) => {
    const nextParams = new URLSearchParams(searchParams);
    const trimmed = nextQuery.trim();

    if (trimmed) {
      nextParams.set('query', trimmed);
    } else {
      nextParams.delete('query');
    }

    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const deviceData = useMemo(() => {
  return devices
    .filter((device) => {
      if (kind === '플러그') {
        return Boolean(device.plug_id);
      }

      if (kind === '스위치') {
        return device.swich_status !== null &&
          device.swich_status !== undefined;
      }

      if (kind === '감지기') {
        return Boolean(device.motion_id);
      }

      return false;
    })
    .map((device) => {
      let rawId;
      let status;
      let recent;

      if (kind === '플러그') {
        rawId = device.plug_id;
        status = getStatusText(device.plug_status);
        recent = device.last_power_received;
      } else if (kind === '스위치') {
        rawId = device.swich_id;
        status = getStatusText(device.swich_status);
        recent = device.last_switch_action;
      } else {
        rawId = device.motion_id;
        status = getStatusText(device.motion_status);
        recent = device.last_motion_action;
      }

      const displayId =
        kind === '플러그'
          ? String(rawId ?? '-').replace(PLUG_PREFIX, '')
          : String(rawId ?? '-').replace(/^DAWONDNS-/, '');

      const location = [
        device.address,
        device.detail_address
      ].filter(Boolean).join(' ');

      return {
        key: `${kind}-${device.id}`,
        id: displayId,
        rawId: String(rawId ?? ''),
        user: device.name || device.plug_name || '-',
        location: location || '-',
        status,
        recent,
        device
      };
    });
}, [devices, kind]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();

    if (!keyword) return deviceData;

    return deviceData.filter((item) =>
      [
        item.id,
        item.rawId,
        item.user,
        item.location,
        item.device.id
      ].some((value) =>
        String(value ?? '').toLowerCase().includes(keyword)
      )
    );
  }, [deviceData, query]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, pages);

  const visible = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const startPage =
    Math.floor((safePage - 1) / PAGE_GROUP_SIZE) *
      PAGE_GROUP_SIZE + 1;

  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, pages);

  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );

  const handleDetail = (item) => {
    navigate(`/device/${item.device.id}`, {
      state: { device: item.device }
    });
  };

  return (
    <div className="dm-page">
      <div className="dm-heading">
        <div>
          <h2>
            <Cpu size={23} /> 장비 관리
          </h2>
          <p>장비 종류별 설치 및 상태 현황</p>
        </div>

        <span className="dm-total">
          {kind} 조회 {deviceData.length}건
        </span>
      </div>

      <div className="dm-panel">
        <div className="dm-toolbar">
          <div
            className="dm-tabs"
            role="group"
            aria-label="장비 종류"
          >
            {kinds.map((item) => (
              <button
                key={item}
                type="button"
                className={kind === item ? 'dm-selected' : ''}
                onClick={() => changeKind(item)}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="dm-search">
            <Search size={17} />

            <input
	  	  aria-label="장비 검색"
		  placeholder="장비 ID, 사용자, 설치위치 검색"
		  value={inputQuery}
		  onChange={(event) => setInputQuery(event.target.value)}
		  onKeyDown={(event) => {
		    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
		      changeQuery(inputQuery);
		    }
		  }}
            />
		<button
		  type="button"
		  onClick={() => changeQuery(inputQuery)}
		>
		  검색
		</button>
            <button
              type="button"
              onClick={() =>{ setInputQuery(''); changeQuery('');}}
            >
              <RotateCcw size={15} /> 초기화
            </button>
          </div>
        </div>

        <div className="dm-count">
          조회 결과 <strong>{filtered.length}건</strong>
        </div>

        <div className="dm-table-wrap">
          <table className="dm-table">
	    <colgroup>
		    <col style={{ width: '5%' }} />
		    <col style={{ width: '19%' }} />
		    <col style={{ width: '9%' }} />
		    <col style={{ width: '35%' }} />
		    <col style={{ width: '9%' }} />
		    <col style={{ width: '14%' }} />
		    <col style={{ width: '9%' }} />
	    </colgroup>
            <thead>
              <tr>
                <th>No</th>
                <th>장비 ID</th>
                <th>사용자</th>
                <th>설치위치</th>
                <th>상태</th>
                <th>최근 활동 시간</th>
                <th>경과 시간</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="dm-empty">
                    장비 목록을 불러오는 중입니다.
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="dm-empty">
                    {error}
                  </td>
                </tr>
              ) : visible.length ? (
                visible.map((item, index) => (
                  <tr
                    key={item.key}
                    onClick={() => handleDetail(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleDetail(item);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      {(safePage - 1) * PAGE_SIZE + index + 1}
                    </td>

                    <td className="dm-id" title={item.rawId}>
                      {item.id}
                    </td>

                    <td>{item.user}</td>

                    <td className="dm-location" title={item.location}>{item.location}</td>

                    <td>
                      <span
                        className={`dm-status ${
                          item.status === '정상'
                            ? 'dm-ok'
                            : item.status === '비정상'
                              ? 'dm-bad'
                              : ''
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

			<td className="dm-time">
			  {item.recent || '-'}
			</td>
	
			<td className="dm-time">
			  {getElapsedTime(item.recent)}
			</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="dm-empty">
		      검색 결과가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="dm-footer">
          <span>
            {filtered.length
              ? (safePage - 1) * PAGE_SIZE + 1
              : 0}
            –
            {Math.min(safePage * PAGE_SIZE, filtered.length)}
            {' / '}
            {filtered.length}건
          </span>

          <div className="dm-pagination">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => changePage(safePage - 1)}
            >
              이전
            </button>

            {pageNumbers.map((number) => (
              <button
                key={number}
                type="button"
                className={number === safePage ? 'dm-current' : ''}
                onClick={() => changePage(number)}
              >
                {number}
              </button>
            ))}

            <button
              type="button"
              disabled={safePage === pages}
              onClick={() => changePage(safePage + 1)}
            >
              다음
            </button>
          </div>
        </div>
      </div>

      <p className="dm-note">
        ※ 플러그·스위치 상태는 실제 장비 API를 사용합니다.
        스위치 목록은 현재 API에서 상태가 제공되는 장비 기준이며,
        실제 설치 대수와 다를 수 있습니다.
        감지기 및 최근 활동 시간은 연동 대기 중입니다.
      </p>
    </div>
  );
}
